/**
 * MOCK permit-type list (Permits & Licensing, roadmap module 6 — Category is reused as the
 * permit-type taxonomy, Kind="permit" — SAME entity pattern as question topics / channels / KORA
 * record types / case violation types). Mirrors koraRecordTypes.ts's shape/role.
 *
 * Permits fidelity Phase A (docs/features/permits-fidelity.md §A.1/§A.6, DECIDED open-question #2):
 * these are the SIX real Linn Valley permit types, each carrying its FormSchema — "Sign"/"Business
 * License"/"Residential Addition" etc. are NOT real Linn Valley types and are never seeded/mocked
 * (mock-cleanup build task). Ids match the backend seed (`cmd/seed-permit-types`) so a mocked
 * FormValues answer set and a live one exercise the identical field-key set.
 *
 * USE_MOCKS-fallback only — every live-aware surface reads permit types from the real Kind=permit
 * Category entities instead: the resident ApplyForPermitPage via ThemeProvider's `permitTypes` (fed
 * by GET /api/public/embed-config, server-split by Kind, formSchema/requiredDocs included); staff
 * surfaces (PermitDetailPage, CategoriesAdmin's "Permit types" tab) via a live admin/embed read. This
 * mock list is the last-resort fallback if a live fetch returns an empty list (a brand-new tenant
 * with no permit types configured yet) — never imported directly by a resident/staff page.
 */
import type { FormSchema, InspectionTypeDef, RequiredDoc } from "./types.js";

export interface PermitType {
  id: string;
  label: string;
  formSchema?: FormSchema;
  requiredDocs?: RequiredDoc[];
  /** Per-type application-fee override in whole dollars (permit-type-fee-override): `0` = free
   *  (e.g. Water), a positive number overrides the town default, `undefined` = inherit the town's
   *  default app fee. Mirrors `shared.Category.PermitAppFeeOverride`; the SERVER is authoritative at
   *  submit (shared.EffectivePermitAppFee) — this is only the pre-submit display value. */
  permitAppFeeOverride?: number;
  /** Structured Inspections (permit-inspections.md Phase 1) — the ordered expected-inspection
   *  checklist for this type. Undefined/empty means "no checklist" (e.g. Water). Read by the
   *  resident tracking page (via ThemeProvider's permitTypes, fed by embed-config) to render the
   *  read-only checklist. */
  inspectionTypes?: InspectionTypeDef[];
}

// ---- Universal base section (prepended to every type, ♻️ mirrors
// cmd/seed-permit-types/main.go withUniversalBase exactly — same field keys, same showWhen wiring)

const UNIVERSAL_BASE_SECTION = {
  key: "applicant",
  title: "Applicant & Contractor",
  fields: [
    { key: "owner_name", label: "Owner name", type: "text" as const, required: true, maxLen: 200 },
    {
      key: "owner_mailing_address",
      label: "Owner mailing address",
      type: "text" as const,
      required: true,
      help: "If different from the site address",
      maxLen: 500,
    },
    { key: "owner_phone", label: "Owner phone", type: "text" as const, maxLen: 32 },
    { key: "owner_email", label: "Owner email", type: "text" as const, required: true, maxLen: 320 },
    { key: "lot_number", label: "Lot #", type: "text" as const, maxLen: 64 },
    { key: "contractor_self", label: "I'm doing my own work (Self)", type: "checkbox" as const },
    {
      key: "contractor_name",
      label: "General/Builder contractor name",
      type: "text" as const,
      maxLen: 200,
      showWhen: { field: "contractor_self", equals: "false" },
    },
    {
      key: "contractor_reg_number",
      label: "General/Builder contractor registration #",
      type: "text" as const,
      maxLen: 64,
      help: "Verified at review (live registry validation is a later fast-follow)",
      showWhen: { field: "contractor_self", equals: "false" },
    },
    {
      key: "self_contractor_affidavit_ack",
      label: "I acknowledge the Self-Contractor affidavit",
      type: "checkbox" as const,
      required: true,
      help: "Required when doing your own work",
      showWhen: { field: "contractor_self", equals: "true" },
    },
  ],
};

function withUniversalBase(sections: FormSchema["sections"]): FormSchema["sections"] {
  return [UNIVERSAL_BASE_SECTION, ...sections];
}

function universalRequiredDocs(...extra: RequiredDoc[]): RequiredDoc[] {
  return [
    { key: "survey_corner_pin", label: "Survey + corner-pin certificate", kind: "upload" },
    { key: "site_plan", label: "Scaled site plan", kind: "upload" },
    { key: "deed", label: "Deed", kind: "upload" },
    ...extra,
  ];
}

/** shared.TankCountFormKey mirror — the well-known field key the $500/tank impact fee reads. */
export const TANK_COUNT_FORM_KEY = "tank_count";

const BUILDING_SCHEMA: FormSchema = {
  version: 1,
  sections: withUniversalBase([
    {
      key: "sub_contractors",
      title: "Other Contractors",
      help: "Free text — only the General/Builder contractor above is registry-validated",
      fields: [
        { key: "architect_engineer_name", label: "Architect/Engineer — name", type: "text", maxLen: 200 },
        { key: "architect_engineer_phone", label: "Architect/Engineer — phone", type: "text", maxLen: 32 },
        { key: "electrical_contractor_name", label: "Electrical contractor — name", type: "text", maxLen: 200 },
        { key: "electrical_contractor_reg", label: "Electrical contractor — reg #", type: "text", maxLen: 64 },
        { key: "plumbing_contractor_name", label: "Plumbing contractor — name", type: "text", maxLen: 200 },
        { key: "plumbing_contractor_reg", label: "Plumbing contractor — reg #", type: "text", maxLen: 64 },
        { key: "other_contractor_name", label: "Other contractor — name", type: "text", maxLen: 200 },
        { key: "other_contractor_reg", label: "Other contractor — reg #", type: "text", maxLen: 64 },
      ],
    },
    {
      key: "lot",
      title: "Lot",
      fields: [
        { key: "lot_dimensions", label: "Lot dimensions", type: "dimensions", unit: "ft" },
        {
          key: "lot_type",
          label: "Lot type",
          type: "select",
          options: [
            { value: "building", label: "Building" },
            { value: "multipurpose", label: "Multipurpose" },
            { value: "camping", label: "Camping" },
          ],
        },
      ],
    },
    {
      key: "structure_foundation",
      title: "Structure & Foundation",
      fields: [
        {
          key: "structure_type",
          label: "Structure type",
          type: "select",
          options: [
            { value: "wood_frame", label: "Wood Frame" },
            { value: "steel", label: "Steel" },
            { value: "masonry", label: "Masonry" },
            { value: "reinforced_concrete", label: "Reinforced Concrete" },
            { value: "modular", label: "Modular" },
            { value: "manufactured", label: "Manufactured" },
            { value: "other", label: "Other" },
          ],
        },
        {
          key: "manufactured_year",
          label: "Manufacture year",
          type: "number",
          showWhen: { field: "structure_type", equals: "manufactured" },
        },
        {
          key: "foundation_type",
          label: "Foundation",
          type: "select",
          options: [
            { value: "basement_finished", label: "Basement — Finished" },
            { value: "basement_unfinished", label: "Basement — Unfinished" },
            { value: "crawl", label: "Crawl" },
            { value: "slab", label: "Slab" },
            { value: "piers", label: "Piers" },
          ],
        },
      ],
    },
    {
      key: "site_elements",
      title: "Site Elements",
      help: "Check any that apply — checking an item reveals its details",
      fields: [
        {
          key: "site_elements",
          label: "Site elements",
          type: "checkbox-group",
          options: [
            { value: "garage", label: "Garage (attached/detached)" },
            { value: "carport", label: "Carport (wood/metal)" },
            { value: "accessory_skid_shed", label: "Accessory / Skid Shed" },
            { value: "room_additions", label: "Room Additions" },
            { value: "decks_porches", label: "Decks/Porches" },
            { value: "holding_tanks", label: "Holding Tanks (water/sewer)" },
            { value: "rural_poa_water", label: "Rural/POA Water" },
            { value: "yard_sprinkler", label: "Yard Sprinkler" },
            { value: "sewer_system", label: "Sewer System" },
            { value: "propane_tank", label: "Propane Tank" },
            { value: "structural_concrete", label: "Structural Concrete (flatwork/boat ramp/dock abutment)" },
            { value: "retaining_wall", label: "Retaining Wall (sea wall/riprap)" },
            { value: "fence", label: "Fence" },
            { value: "dock", label: "Dock" },
            { value: "gazebo", label: "Gazebo" },
            { value: "pool", label: "Pool" },
            { value: "hot_tub", label: "Hot Tub" },
            { value: "lot_clearing", label: "Lot Clearing" },
            { value: "culvert_driveway", label: "Culvert/Driveway" },
            { value: "fireplace", label: "Fireplace (metal/masonry)" },
            { value: "remodel", label: "Remodel/Repair" },
            { value: "exterior_finish", label: "Exterior Finish" },
            { value: "other", label: "Other" },
          ],
        },
        {
          key: "accessory_affidavit_ack",
          label: "I acknowledge the Accessory-Building affidavit",
          type: "checkbox",
          required: true,
          showWhen: { field: "site_elements", equals: "accessory_skid_shed" },
        },
        {
          key: "holding_tank_size",
          label: "Holding tank size",
          type: "number",
          unit: "gal",
          help: "1,500 gal minimum",
          showWhen: { field: "site_elements", equals: "holding_tanks" },
        },
        {
          key: "fence_height_material",
          label: "Fence height / material",
          type: "text",
          showWhen: { field: "site_elements", equals: "fence" },
        },
        {
          key: "remodel_explanation",
          label: "Remodel/repair explanation",
          type: "textarea",
          showWhen: { field: "site_elements", equals: "remodel" },
        },
        {
          key: "other_element_explanation",
          label: "Other — explanation",
          type: "textarea",
          showWhen: { field: "site_elements", equals: "other" },
        },
      ],
    },
    {
      key: "electrical_service",
      title: "Electrical Service",
      fields: [
        {
          key: "electrical_amps",
          label: "Amps",
          type: "select",
          unit: "amps",
          options: [
            { value: "100", label: "100" },
            { value: "200", label: "200" },
            { value: "other", label: "Other" },
          ],
        },
        {
          key: "electrical_upgrade",
          label: "Upgrade",
          type: "select",
          unit: "amps",
          options: [
            { value: "none", label: "None" },
            { value: "200", label: "Upgrade to 200" },
            { value: "400", label: "Upgrade to 400" },
          ],
        },
      ],
    },
    {
      key: "dates_cost",
      title: "Dates & Estimated Cost",
      fields: [
        { key: "begin_date", label: "Begin date", type: "date" },
        { key: "completion_date", label: "Estimated completion date", type: "date" },
        { key: "estimated_project_cost", label: "Estimated project cost", type: "number", unit: "$", required: true },
      ],
    },
  ]),
};

const SKID_SHED_SCHEMA: FormSchema = {
  version: 1,
  sections: withUniversalBase([
    {
      key: "shed",
      title: "Shed Details",
      fields: [
        { key: "shed_dimensions", label: "Dimensions", type: "dimensions", unit: "ft", required: true },
        { key: "shed_description", label: "Description", type: "textarea", required: true },
        { key: "shed_age", label: "Age of shed", type: "number", unit: "years" },
        {
          key: "accessory_affidavit_ack",
          label: "I acknowledge the Accessory-Building affidavit",
          type: "checkbox",
          required: true,
        },
      ],
    },
  ]),
};

const DOCK_SCHEMA: FormSchema = {
  version: 1,
  sections: withUniversalBase([
    {
      key: "dock_details",
      title: "Dock Details",
      fields: [
        {
          key: "size_limits_note",
          label: "Size limits",
          type: "section-note",
          help: "120–450 sq ft on the Main lake; 64–120 sq ft on the 7-acre/10-acre lakes; no limit on the 5-acre lake. GFI-in-conduit required. Lot # must be on the lake side.",
        },
        {
          key: "lake",
          label: "Lake",
          type: "select",
          options: [
            { value: "main", label: "Main" },
            { value: "7_acre", label: "7-acre" },
            { value: "10_acre", label: "10-acre" },
            { value: "5_acre", label: "5-acre" },
          ],
        },
        {
          key: "construction_type",
          label: "Construction type",
          type: "select",
          options: [
            { value: "new", label: "New" },
            { value: "replacement", label: "Replacement" },
            { value: "repair", label: "Repair" },
            { value: "modification", label: "Modification" },
          ],
        },
        {
          key: "dismantled_or_resold",
          label: "Old dock — dismantled or resold?",
          type: "select",
          options: [
            { value: "dismantled", label: "Dismantled" },
            { value: "resold", label: "Resold" },
          ],
          showWhen: { field: "construction_type", equals: "replacement" },
        },
        {
          key: "relocated_address",
          label: "Relocated address (if resold)",
          type: "text",
          maxLen: 500,
          showWhen: { field: "construction_type", equals: "replacement" },
        },
        {
          key: "dock_electrical",
          label: "Electrical",
          type: "select",
          options: [
            { value: "new", label: "New Service" },
            { value: "existing", label: "Existing at dock" },
          ],
        },
        { key: "dock_dimensions", label: "Dimensions", type: "dimensions", unit: "ft" },
        { key: "dock_materials", label: "Materials", type: "text", maxLen: 200 },
        { key: "dock_work_description", label: "Work description", type: "textarea", required: true },
      ],
    },
  ]),
};

const ELECTRICAL_SCHEMA: FormSchema = {
  version: 1,
  sections: withUniversalBase([
    {
      key: "service",
      title: "Service Details",
      fields: [
        {
          key: "lot_type",
          label: "Lot type",
          type: "select",
          options: [
            { value: "r1_building", label: "R-1 (Building)" },
            { value: "r2_multipurpose_camping", label: "R-2 (Multipurpose/Camping)" },
          ],
        },
        {
          key: "service_type",
          label: "Upgrade / New service",
          type: "select",
          options: [
            { value: "upgrade", label: "Upgrade" },
            { value: "new", label: "New Service" },
          ],
        },
        {
          key: "run_type",
          label: "Underground / Overhead",
          type: "select",
          options: [
            { value: "underground", label: "Underground" },
            { value: "overhead", label: "Overhead" },
          ],
        },
        {
          key: "amps",
          label: "Amps",
          type: "select",
          unit: "amps",
          options: [
            { value: "100", label: "100" },
            { value: "200", label: "200" },
          ],
        },
        { key: "work_description", label: "Work description", type: "textarea", required: true },
        { key: "evergy_contact_name", label: "Evergy contact name", type: "text", maxLen: 200 },
        { key: "evergy_contact_phone", label: "Evergy contact phone", type: "text", maxLen: 32 },
        { key: "electrical_contractor_name", label: "Electrical contractor name", type: "text", maxLen: 200 },
        { key: "electrical_contractor_reg", label: "Electrical contractor reg #", type: "text", maxLen: 64 },
      ],
    },
  ]),
};

const SEWER_WATER_TANK_SCHEMA: FormSchema = {
  version: 1,
  sections: withUniversalBase([
    {
      key: "tank",
      title: "Tank Details",
      help: "Precast, Kansas-certified tanks, 1,500 gal minimum",
      fields: [
        {
          key: "tank_types",
          label: "Tank type(s)",
          type: "checkbox-group",
          options: [
            { value: "sewer", label: "Sewer tank" },
            { value: "water", label: "Water tank" },
          ],
        },
        { key: "tank_size", label: "Tank size", type: "number", unit: "gal", help: "1,500 gal minimum" },
        {
          key: TANK_COUNT_FORM_KEY,
          label: "Number of holding tanks",
          type: "number",
          required: true,
          help: "Drives the $500-per-tank impact fee",
        },
        { key: "installation_contractor_name", label: "Installation contractor name", type: "text", maxLen: 200 },
        { key: "installation_contractor_reg", label: "Installation contractor reg #", type: "text", maxLen: 64 },
      ],
    },
  ]),
};

const LOT_CLEARING_CULVERT_SCHEMA: FormSchema = {
  version: 1,
  sections: withUniversalBase([
    {
      key: "work",
      title: "Work Details",
      fields: [
        {
          key: "work_type",
          label: "Work type",
          type: "select",
          required: true,
          options: [
            { value: "lot_clearing", label: "Lot Clearing" },
            { value: "culvert", label: "Culvert" },
          ],
        },
        { key: "work_description", label: "Work description", type: "textarea", required: true },
      ],
    },
  ]),
};

/** Structured Inspections (permit-inspections.md Phase 1, open question #1): Building is the only
 *  mock type seeded with a checklist — a multi-step inspection sequence is real for a house, while
 *  every other mock type is realistically single-final-or-none (the doc's own framing). Mirrors
 *  cmd/seed-permit-types' real Building checklist exactly so a mocked progress view and a live one
 *  exercise the identical slot-key set. */
const BUILDING_INSPECTION_TYPES: InspectionTypeDef[] = [
  { key: "footing", label: "Footing" },
  { key: "framing", label: "Framing" },
  { key: "rough_in_electrical", label: "Rough-in — Electrical" },
  { key: "rough_in_plumbing", label: "Rough-in — Plumbing" },
  { key: "insulation", label: "Insulation" },
  { key: "final", label: "Final" },
];

export const MOCK_PERMIT_TYPES: PermitType[] = [
  {
    id: "permit-building",
    label: "Building",
    formSchema: BUILDING_SCHEMA,
    requiredDocs: universalRequiredDocs(),
    inspectionTypes: BUILDING_INSPECTION_TYPES,
  },
  {
    id: "permit-skid-shed",
    label: "Skid Shed",
    formSchema: SKID_SHED_SCHEMA,
    requiredDocs: universalRequiredDocs({ key: "shed_photos", label: "Photos of the shed", kind: "upload" }),
  },
  {
    id: "permit-dock",
    label: "Dock",
    formSchema: DOCK_SCHEMA,
    requiredDocs: universalRequiredDocs(),
  },
  {
    id: "permit-electrical",
    label: "Electrical",
    formSchema: ELECTRICAL_SCHEMA,
    requiredDocs: universalRequiredDocs({
      key: "evergy_contact",
      label: "Contact Evergy before a permit can be issued",
      kind: "external-prereq",
      help: "The electric company must be contacted before any permit can be issued",
    }),
  },
  {
    id: "permit-sewer-water-tank",
    label: "Sewer / Water Tank",
    formSchema: SEWER_WATER_TANK_SCHEMA,
    requiredDocs: universalRequiredDocs(),
  },
  {
    id: "permit-lot-clearing-culvert",
    label: "Lot Clearing / Culvert",
    formSchema: LOT_CLEARING_CULVERT_SCHEMA,
    requiredDocs: universalRequiredDocs(),
  },
];
